import moment from 'moment';

import Icon          from 'app/components/common/icon';
import colorsHelpers from 'app/helpers/colors';
import formsHelpers  from 'app/helpers/forms';
import Ps            from 'app/helpers/publish-statuses';
import rubHelpers    from 'app/helpers/rubrics';



const DEFAULT_ACK_TEXT = 'I have read and agree to the terms.';
const MAX_STAGE_COUNT = 12; // needs to line up with the available number of colors

const AppStatuses = {
  DRAFT: 'draft',
  REVIEW: 'review',
  DECLINED: 'declined',
  APPROVED: 'approved',
};

const StageTypes = {
  USER_DEFINED: 'user-defined',
  SYSTEM_DECIDE: 'system-decide',
  SYSTEM_APPROVED: 'system-approved',
  SYSTEM_DECLINED: 'system-declined',
};

const TaskTypes = {
  PASS_FAIL: 'pass-fail',
  VOTE: 'vote',
  SCORE: 'score',
  FINAL: 'final',
  PAYMENT: 'payment',
  COMPLETE: 'complete',
  FILE: 'file',
  NOTIFY: 'notify',
  FORM: 'form',
  SIGNATURE: 'signature',
  NONPROFIT_CHECK: 'nonpofit-check',
};

const TimelineTypes = {
  SUBMITTED: 'submitted',
  COMMENT: 'comment',
  ATTACHMENT: 'attachment', // a comment with files attached
  ASSIGNMENT: 'assignment',
  TASK_RESULT: 'task-result',
  NEW_STAGE: 'new-stage',
};

const FieldPresenceTypes = {
  OFF: 'off',
  OPTIONAL: 'optional',
  REQUIRED: 'required',
};

const getTodayDateStr = (program) => program.company?.todayDateStr || moment().format('YYYY-MM-DD');

const isUpcoming = (program) => {
  if (!program.openDateStr) return false;
  const todayDateStr = getTodayDateStr(program);
  return program.openDateStr > todayDateStr;
};

const isEnded = (program) => {
  if (!program.closeDateStr) return false;
  const todayDateStr = getTodayDateStr(program);
  return todayDateStr > program.closeDateStr;
};


// TODO: rename to `isOpen`
const isWithinActiveRange = (program) => {
  if (isEnded(program)) return false;
  if (isUpcoming(program)) return false;
  return true;
};

const isActive = (program) => {
  return program?.publishStatus === Ps.ACTIVE;
};

const isActiveAndOpen = (program) => {
  return isActive(program) && isWithinActiveRange(program);
};

const endsInDays = (program) => {
  if (!program.closeDateStr) return null;
  const todayDateStr = getTodayDateStr(program);
  return moment(program.closeDateStr).diff(moment(todayDateStr), 'days');
};

const opensInDays = (program) => {
  if (!program.openDateStr) return null;
  const todayDateStr = getTodayDateStr(program);
  return moment(program.openDateStr).diff(moment(todayDateStr), 'days');
};

const getAppProgress = (forms, submissions) => {
  const obj = {
    total: {
      formCount: 0,
      submissionCount: 0,
      fieldCount: 0,
      requiredFieldCount: 0,
      presentAnswerCount: 0,
      validAnswerCount: 0,
      requiredAnswerCount: 0,
      fieldsComplete: true,
      formsComplete: true,
      isComplete: true,
    },
  };

  forms.forEach((form) => {
    const fsub = submissions.find(fs => fs.formId === form.id);
    // init the form row
    const row = obj[form.id] = {
      formCount: 1,
      submissionCount: fsub ? 1 : 0,
      fieldCount: 0,
      requiredFieldCount: 0,
      presentAnswerCount: 0,
      validAnswerCount: 0,
      requiredAnswerCount: 0,
    };
    // increment the form row
    form.formFields.forEach((field) => {
      row.fieldCount += 1;
      if (field.isRequired) row.requiredFieldCount += 1;
      const ans = (fsub?.formAnswers || []).find(fa => fa.formFieldId === field.id);
      if (ans) row.presentAnswerCount += 1;
      if (formsHelpers.isValidAnswer(field, ans)) {
        row.validAnswerCount += 1;
        if (field.isRequired) row.requiredAnswerCount += 1;
      }
    });
    row.fieldsComplete = row.requiredAnswerCount >= row.requiredFieldCount;
    row.formsComplete  = row.submissionCount     >= row.formCount;
    row.isComplete = row.fieldsComplete && row.formsComplete;
    row.progressPercent = Math.round(Math.min((row.submissionCount + row.requiredAnswerCount) / (row.formCount + row.requiredFieldCount), 1) * 100);
    // increment the total row
    ['formCount','submissionCount','fieldCount','requiredFieldCount','presentAnswerCount','validAnswerCount','requiredAnswerCount'].forEach((key) => {
      obj.total[key] += row[key];
    });
    obj.total.fieldsComplete = obj.total.fieldsComplete && row.fieldsComplete;
    obj.total.formsComplete  = obj.total.formsComplete  && row.formsComplete;
    obj.total.isComplete  = obj.total.isComplete  && row.isComplete;
  });

  // calculate progress
  obj.progressPercent = Math.round(Math.min((obj.total.submissionCount + obj.total.requiredAnswerCount) / (obj.total.formCount + obj.total.requiredFieldCount), 1) * 100);

  return obj;
};

const ProgramStatuses = {
  ENDED: 'ended',
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
};

const getProgramStatus = (program) => {
  const { openTs, openDateStr, closeTs, closeDateStr } = program;
  if (closeTs && moment().isAfter(moment.unix(closeTs))) return ProgramStatuses.ENDED;
  if (openTs && moment().isBefore(moment.unix(openTs))) return ProgramStatuses.UPCOMING;
  if (closeTs && openTs) return ProgramStatuses.ACTIVE;
  if (closeDateStr && moment().startOf('day').isAfter(moment(closeDateStr))) return ProgramStatuses.ENDED;
  if (openDateStr && moment().isBefore(moment(openDateStr))) return ProgramStatuses.UPCOMING;
  return ProgramStatuses.ACTIVE;
};

const getProgramStatusDetails = (program) => {
  const status = getProgramStatus(program);
  if (status === ProgramStatuses.UPCOMING) return ['Upcoming', 'blue'];
  if (status === ProgramStatuses.ENDED) return ['Ended', 'gray'];
  return ['Live', 'green'];
};

const colors = (stageCount) => {
  const grad = {
     2: [],
     3: ['#7837c6'],
     4: ['#7837c6','#0095b8'],
     5: ['#7837c6','#0085bd','#00a7b2'],
     6: ['#7837c6','#007bc8','#0095b8','#00b1ab'],
     7: ['#7837c6','#0071da','#008cba','#009fb5','#00b7a4'],
     8: ['#7837c6','#0065ec','#0085bd','#0095b8','#00a7b2','#00bc9f'],
     9: ['#7837c6','#1860e8','#0080c2','#008eb9','#009cb6','#00adae','#00bf9b'],
    10: ['#7837c6','#315ce5','#007bc8','#0089bb','#0095b8','#00a2b4','#00b1ab','#00c197'],
    11: ['#7837c6','#3e58e2','#0076d0','#0085bd','#0090b9','#009bb7','#00a7b2','#00b4a7','#00c394'],
    12: ['#7837c6','#4755e0','#0071da','#0081c0','#008cba','#0095b8','#009fb5','#00abb0','#00b7a4','#00c592'],
  }[stageCount];
  return [...grad, colorsHelpers.accentGreen, colorsHelpers.danger];
};


const makeDefaultTaskAttrs = (attrs={}) => {
  return {
    title: '',
    description: '',
    type: TaskTypes.COMPLETE,
    employeeIds: [],
    rubric: null,
    notificationTemplate: null,
    remarksField: FieldPresenceTypes.OPTIONAL,
    fileField: FieldPresenceTypes.OFF,
    autoAssign: true,
    assCount: 1,
    assVisInternalContent: true,
    assVisBudget: false,
    isAutomatic: false,
    employeeIds: [],
    ...attrs,
  };
};

const taskTypeObjs = {
  [TaskTypes.PASS_FAIL]: {
    key: TaskTypes.PASS_FAIL,
    label: 'Pass/fail',
    description: 'Binary gate - use to automate declines.',
    resultLabel: 'Result',
    IconComp: Icon.LikeBubble,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: true,
    allowAssCount: true,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.PASS_FAIL, rubric: rubHelpers.makeRubricPassFail(), ...attrs}),
    forcedAttrs: {notificationTemplate: null, isAutomatic: false},
  },
  [TaskTypes.COMPLETE]: {
    key: TaskTypes.COMPLETE,
    label: 'Task',
    description: 'Collect remarks, upload files, record offline task, etc.',
    resultLabel: '',
    IconComp: Icon.MessageBubbleEmpty,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: true,
    allowAssCount: true,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.COMPLETE, ...attrs}),
    forcedAttrs: {notificationTemplate: null, isAutomatic: false},
  },
  [TaskTypes.FILE]: {
    key: TaskTypes.FILE,
    label: 'File upload',
    description: 'Attach files to the application.',
    resultLabel: '',
    IconComp: Icon.Attachment1,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: true,
    allowAssCount: true,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.FILE, fileField: FieldPresenceTypes.REQUIRED, ...attrs}),
    forcedAttrs: {notificationTemplate: null, isAutomatic: false},
  },
  [TaskTypes.VOTE]: {
    key: TaskTypes.VOTE,
    label: 'Vote',
    description: 'Each reviewer casts their vote.',
    resultLabel: 'Vote',
    IconComp: Icon.RatingStarWinner,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: true,
    allowAssCount: true,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.VOTE, rubric: rubHelpers.makeRubricVote(), title: 'Review & Vote', description: 'Please review the full application and leave your vote.', ...attrs}),
    forcedAttrs: {notificationTemplate: null, isAutomatic: false},
  },
  [TaskTypes.SCORE]: {
    key: TaskTypes.SCORE,
    label: 'Score',
    description: 'Numeric score or rating, averaged across reviewers.',
    resultLabel: 'Score',
    IconComp: Icon.ListNumbers,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: true,
    allowAssCount: true,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.SCORE, rubric: rubHelpers.makeRubricScore(), title: 'Review & Score', description: 'Please review the full application and submit a score based on our guidelines.', ...attrs}),
    forcedAttrs: {notificationTemplate: null, isAutomatic: false},
  },
  [TaskTypes.FINAL]: {
    key: TaskTypes.FINAL,
    label: 'Final decision',
    description: 'Please make a formal decision on whether to approve or decline this application.',
    resultLabel: 'Decision',
    IconComp: Icon.LegalHammer,
    comingSoon: false,
    allowRemarks: true,
    allowFile: false,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.FINAL, title: 'Final decision', ...attrs}),
    forcedAttrs: {notificationTemplate: null, rubric: null, fileField: FieldPresenceTypes.OFF, isAutomatic: false, assVisInternalContent: true, assVisBudget: true, assCount: 1},
  },
  [TaskTypes.PAYMENT]: {
    key: TaskTypes.PAYMENT,
    label: 'Payment',
    description: 'Disburse the award from a linked balance, or mark as paid.',
    resultLabel: 'Payment',
    IconComp: Icon.AccountingBill,
    comingSoon: false,
    allowRemarks: true,
    allowFile: true,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: false,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.PAYMENT, title: 'Send Payment', ...attrs}),
    forcedAttrs: {notificationTemplate: null, rubric: null, fileField: FieldPresenceTypes.OFF, isAutomatic: false, assVisInternalContent: true, assVisBudget: true, assCount: 1},
  },
  [TaskTypes.NOTIFY]: {
    key: TaskTypes.NOTIFY,
    label: 'Notification',
    description: 'Send an email notification to the applicant.',
    resultLabel: 'Sent',
    IconComp: Icon.Envelope,
    comingSoon: true,
    allowRemarks: false,
    allowFile: false,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: true,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.NOTIFY, ...attrs}),
    forcedAttrs: {rubric: null, fileField: FieldPresenceTypes.OFF, remarksField: FieldPresenceTypes.OFF, assCount: 1},
  },
  [TaskTypes.FORM]: {
    key: TaskTypes.FORM,
    label: 'Follow-up form',
    description: 'Send a follow up form to the applicant.',
    resultLabel: 'Submitted',
    IconComp: Icon.TypeCursor,
    comingSoon: true,
    allowRemarks: false,
    allowFile: false,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: true,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.FORM, ...attrs}),
    forcedAttrs: {notificationTemplate: null, rubric: null, fileField: FieldPresenceTypes.OFF, remarksField: FieldPresenceTypes.OFF, assCount: 1},
  },
  [TaskTypes.SIGNATURE]: {
    key: TaskTypes.SIGNATURE,
    label: 'Document signature',
    description: 'Send an agreement out for signing.',
    resultLabel: 'Signed',
    IconComp: Icon.ContentPenWrite3,
    comingSoon: true,
    allowRemarks: false,
    allowFile: false,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: true,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.SIGNATURE, ...attrs}),
    forcedAttrs: {notificationTemplate: null, rubric: null, fileField: FieldPresenceTypes.OFF, remarksField: FieldPresenceTypes.OFF, assCount: 1},
  },
  [TaskTypes.NONPROFIT_CHECK]: {
    key: TaskTypes.NONPROFIT_CHECK,
    label: 'Nonprofit check',
    description: 'Verify 501(c)(3) standing and screening.',
    resultLabel: 'Status',
    IconComp: Icon.TaskChecklistCheck,
    comingSoon: true,
    allowRemarks: true,
    allowFile: true,
    allowRubric: false,
    allowAssCount: false,
    allowAutomatic: true,
    getDefaultAttrs: (attrs={}) => makeDefaultTaskAttrs({type: TaskTypes.NONPROFIT_CHECK, isAutomatic: true, autoAssign: false, ...attrs}),
    forcedAttrs: {},
  },
};

const taskTypeObj = (taskOrType) => {
  if (!taskOrType) return null;
  const type = taskOrType.type || taskOrType;
  return taskTypeObjs[type] || null;
};

const taskMetaLabel = (task) => {
  const taskType = taskTypeObj(task);
  // if ([TaskTypes.FINAL, TaskTypes.PAYMENT].includes(taskType.key)) return '';
  let label = taskType.label;
  if (task.assCount > 1) label += ` ${task.assCount}x`;
  return label;
};

// const getTaskResultDetails = (ass) => {
//   const taskType = ass?.resultTaskDetails?.type || null;
//   if (!taskType) return null;
//   const resultVal = (ass.result || {})[taskType];
//   if (resultVal == null) return null;
// 
//   const isScore = taskType === TaskTypes.SCORE;
//   const isPos   = !!(!isScore && (resultVal > 0));
//   const isNeg   = !!(!isScore && (resultVal < 0));
//   
//   const color = (() => {
//     if (isPos) return 'green';
//     if (isNeg) return 'danger';
//     if (isScore) return 'blue';
//     return 'slate';
//   })();
// 
//   const badgeKey = ({
//     [TaskTypes.PASS_FAIL]: 'Result',
//     [TaskTypes.VOTE]: 'Vote',
//     [TaskTypes.SCORE]: 'Score',
//     [TaskTypes.FINAL]: 'Decision',
//   })[taskType] || null;
// 
//   const badgeVal = (() => {
//     if (taskType === TaskTypes.PASS_FAIL) return isPos ? 'Pass' : 'Fail';
//     if (taskType === TaskTypes.VOTE) {
//       if (resultVal === -2) return 'Strong No';
//       if (resultVal === -1) return 'No';
//       if (resultVal ===  0) return 'Neutral';
//       if (resultVal ===  1) return 'Yes';
//       if (resultVal ===  2) return 'Strong Yes';
//     }
//     if (taskType === TaskTypes.SCORE) return `${resultVal}`;
//     if (taskType === TaskTypes.FINAL) return isPos ? 'Approved' : 'Declined';
//     if (taskType === TaskTypes.PAYMENT) return isPos ? 'Paid' : '';
//     return '';
//   })();
// 
//   return {isScore, isPos, isNeg, taskType, color, badgeKey, badgeVal}
// };

const reviewTaskTypes = [TaskTypes.PASS_FAIL, TaskTypes.VOTE, TaskTypes.SCORE, TaskTypes.COMPLETE, TaskTypes.FILE];
const isReviewType = (taskType) => reviewTaskTypes.includes(taskType);
const mainTaskTypes = [...reviewTaskTypes, TaskTypes.NOTIFY, TaskTypes.FORM, TaskTypes.SIGNATURE, TaskTypes.NONPROFIT_CHECK];

const stageTypeObjs = {
  [StageTypes.USER_DEFINED]: {
    key: StageTypes.USER_DEFINED,
    addableTaskTypes: [...mainTaskTypes],
  },
  [StageTypes.SYSTEM_DECIDE]: {
    key: StageTypes.SYSTEM_DECIDE,
    addableTaskTypes: [...mainTaskTypes],
  },
  [StageTypes.SYSTEM_APPROVED]: {
    key: StageTypes.SYSTEM_APPROVED,
    addableTaskTypes: [...mainTaskTypes, TaskTypes.PAYMENT],
  },
  [StageTypes.SYSTEM_DECLINED]: {
    key: StageTypes.SYSTEM_DECLINED,
    addableTaskTypes: [...mainTaskTypes],
  },
};

const stageTypeObj = (stageOrType) => {
  if (!stageOrType) return null;
  const type = stageOrType.type || stageOrType;
  return stageTypeObjs[type] || null;
};

const hasValidSystemStages = (stages) => {
  if (stages[stages.length - 1].type !== StageTypes.SYSTEM_DECLINED) return false;
  if (stages[stages.length - 2].type !== StageTypes.SYSTEM_APPROVED) return false;
  if (stages[stages.length - 3].type !== StageTypes.SYSTEM_DECIDE) return false;
  return true;
};

export default {
  isUpcoming,
  isEnded,
  isWithinActiveRange,
  isActiveAndOpen,
  endsInDays,
  opensInDays,
  DEFAULT_ACK_TEXT,
  MAX_STAGE_COUNT,
  getAppProgress,
  ProgramStatuses,
  getProgramStatus,
  getProgramStatusDetails,
  colors,
  taskMetaLabel,
  AppStatuses,
  TaskTypes,
  StageTypes,
  TimelineTypes,
  FieldPresenceTypes,
  // getTaskResultDetails,
  hasValidSystemStages,
  taskTypeObjs,
  taskTypeObj,
  stageTypeObjs,
  stageTypeObj,
  reviewTaskTypes,
  isReviewType,
};
